import { CheckoutModel, CorporatePass, PaymentStatus, V2PaymentPrepareRequest, V2PaymentResponse } from '@api';
import { AxiosResponse } from 'axios';
import { combineEpics, Epic } from 'redux-observable';
import { Observable } from 'rxjs';
import { IAction } from 'src/js/actions/IshAction';
import { FULFILLED } from 'src/js/common/actions/ActionUtils';
import { Create, ProcessError, Request } from 'src/js/common/epics/EpicUtils';
import {
  EpicCompleteStripePaymentRedirect
} from 'src/js/enrol/containers/payment/epics/EpicCompleteStripePaymentRedirect';
import { EpicCompleteWindcavePayment } from 'src/js/enrol/containers/payment/epics/EpicCompleteWindcavePayment';
import { EpicEwayPayment } from 'src/js/enrol/containers/payment/epics/EpicEwayPayment';
import { EpicSquarePayment } from 'src/js/enrol/containers/payment/epics/EpicSquarePayment';
import { EpicStripePayment } from 'src/js/enrol/containers/payment/epics/EpicStripePayment';
import CheckoutApiService from 'src/js/enrol/services/CheckoutApiService';
import { IshState } from 'src/js/services/IshState';
import { changePhase, getAmount, togglePayNowVisibility } from '../../../actions/Actions';
import { ProcessCheckoutModel } from '../../../epics/EpicProceedToPayment';
import { Phase } from '../../../reducers/State';
import CheckoutService, {
  BuildCheckoutModelRequest,
  BuildWaitingCoursesResult
} from '../../../services/CheckoutService';
import {
  applyCorporatePass,
  CHECKOUT_RESET_PAYMENT_SESSION,
  generateWaitingCoursesResultData,
  GET_CORPORATE_PASS_REQUEST,
  PROCESS_PAYMENT_V2,
  PROCESS_PAYMENT_V2_FAILED_STATUS,
  resetPaymentState,
  setPaymentData,
  SUBMIT_PAYMENT_CORPORATE_PASS,
  SUBMIT_PAYMENT_FOR_WAITING_COURSES,
} from '../actions/Actions';
import { GetPaymentStatus } from './EpicGetPaymentStatus';
import { EpicValidatePayment } from './EpicValidatePayment';

const SubmitPaymentForWaitingCoursesRequest: Request<any, IshState> = {
  type: SUBMIT_PAYMENT_FOR_WAITING_COURSES,
  getData: CheckoutService.processPayment,
  processData: (response: any, state: IshState): IAction<any>[] | Observable<any> => {
    return CheckoutService.processPaymentResponse(
      { status: PaymentStatus.SUCCESSFUL_WAITING_COURSES },
      [generateWaitingCoursesResultData(BuildWaitingCoursesResult.fromState(state))],
    );
  },
  processError: CheckoutService.prcessPaymentError
};

const SubmitPaymentForWaitingCourses: Epic<any, any> = Create(SubmitPaymentForWaitingCoursesRequest);

/**
 * Init PaymentState and Start Payment Process by Corporate Pass
 */
const SubmitPaymentCorporatePassRequest: Request<any, IshState> = {
  type: SUBMIT_PAYMENT_CORPORATE_PASS,
  getData: (payload: any, state: IshState): Promise<any> => {
    return CheckoutService.submitPaymentCorporatePass(payload, state);
  },
  processData: (): IAction<any>[] | Observable<any> => {
    return CheckoutService.processPaymentResponse({ status: PaymentStatus.SUCCESSFUL_BY_PASS });
  },
};

const SubmitPaymentCorporatePass: Epic<any, any> = Create(SubmitPaymentCorporatePassRequest);

const corporatePassRequest: Request<CorporatePass, IshState> = {
  type: GET_CORPORATE_PASS_REQUEST,
  getData: (payload: any, state: IshState): Promise<CorporatePass> => {
    return CheckoutService.getCorporatePass(payload, state);
  },
  processData: (response: CorporatePass): IAction<any>[] | Observable<any> => {
    return [
      { type: FULFILLED(GET_CORPORATE_PASS_REQUEST) },
      applyCorporatePass(response),
      getAmount(),
      togglePayNowVisibility(false),
    ];
  },

};

const GetCorporatePass: Epic<any, any> = Create(corporatePassRequest);

const processPaymentV2: Request<V2PaymentResponse, never> = {
  type: PROCESS_PAYMENT_V2,
  getData:CheckoutService.processPayment,
  processData: (response: V2PaymentResponse) => CheckoutService.processPaymentResponse(response, []),
  processError: CheckoutService.prcessPaymentError
};

const ProcessPaymentV2: Epic<any, any> = Create(processPaymentV2);

const resetPaymentSession: Request<V2PaymentResponse, never> = {
  type: CHECKOUT_RESET_PAYMENT_SESSION,
  getData: async (p, state: IshState) => {
    const paymentRequest: V2PaymentPrepareRequest = {
      checkoutModelRequest: await BuildCheckoutModelRequest.fromState(state),
      ccAmount: state.checkout.amount.ccPayment
    };

    return CheckoutApiService.createSession(paymentRequest, state.checkout.payerId, state.checkout.payment.sessionId)
  },
  processData: (response: V2PaymentResponse)=> [setPaymentData(response)],
  processError: CheckoutService.prcessPaymentError
};

const ResetPaymentSession: Epic<any, any> = Create(resetPaymentSession);

const processPaymentV2Status: Request<V2PaymentResponse, IshState> = {
  type: PROCESS_PAYMENT_V2_FAILED_STATUS,
  getData: (p, state: IshState) => CheckoutApiService.getStatus(state.checkout.payment.sessionId),
  processData: (response: V2PaymentResponse): IAction<any>[] | Observable<any> => {
    response.status = 'FAILED';
    return CheckoutService.processPaymentResponse(response);
  },
  processError: (response: AxiosResponse): IAction<any>[] => {
    const data: any = response?.data;
    if (data && data.payerId && data.amount && data.contactNodes) {
      return [...ProcessCheckoutModel.process(data as CheckoutModel), ...ProcessError(response)];
    } else {
      return [
        changePhase(Phase.Payment),
        resetPaymentState(),
        ...ProcessError(response),
      ];
    }
  },
};

const ProcessPaymentV2Status: Epic<any, any> = Create(processPaymentV2Status);

export const EpicPayment = combineEpics(
  SubmitPaymentCorporatePass,
  SubmitPaymentForWaitingCourses,
  GetPaymentStatus,
  EpicValidatePayment,
  GetCorporatePass,
  ResetPaymentSession,
  ProcessPaymentV2,
  ProcessPaymentV2Status,
  EpicCompleteStripePaymentRedirect,
  EpicCompleteWindcavePayment,
  EpicStripePayment,
  EpicSquarePayment,
  EpicEwayPayment
);
